import { useMemo, useRef } from "react";

import { Link } from "../link";
import { ResponsiveValue } from "../../types";
import { Stack } from "@vinomofo/components";
import styled from "styled-components";
import { useInstantSearch } from "../../hooks/use-instantsearch";
import { useLocation } from "@reach/router";
import useWindowSize from "../../hooks/use-window-size";
import { storefrontNavigationItemProps } from "../../types/vino-data-types";
import { COLORS } from "../../styles";
import { hasSpiritsInPath } from "../../utils/is-page";

const WHISKYMOFO_HEADER_ENABLED =
  process.env.GATSBY_ENABLE_WHISKYMOFO_HEADER === "true";
export interface HeaderCategoryNavProps {
  fontSize?: ResponsiveValue<number>;
  menuItems?: storefrontNavigationItemProps[];
}

const NavLink = styled(Link)<{
  isActive?: boolean;
  isWhiskymofoEnabled?: boolean;
}>`
  display: inline-block;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  color: ${({ isActive, isWhiskymofoEnabled }) =>
    isActive
      ? isWhiskymofoEnabled
        ? COLORS.mofoGold
        : COLORS.mofoGreen
      : null};
  &:hover {
    color: ${({ isWhiskymofoEnabled }) =>
      isWhiskymofoEnabled ? COLORS.mofoGold : COLORS.mofoGreen};
  }
`;

const StyledNav = styled.nav<{ withShadow?: boolean }>`
  padding: 0 5px;
  margin: 0;
  list-style: none;
  display: flex;
  justify-content: space-between;
  width: 100%;
  overflow-y: hidden;
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  scrollbar-width: none;
  // position: relative;

  &::-webkit-scrollbar {
    display: none;
  }

  @media ${({ theme }) => theme.mq.max.tablet} {
    &::before {
      content: "";
      position: absolute;
      right: -1px;
      width: 70px;
      background: linear-gradient(to right, rgba(17, 17, 17, 0) 0, #111 100%);
      pointer-events: none;
      opacity: 1;
      transition: opacity 0.3s ease-in-out;
      z-index: 0;
      height: 15px;
      display: ${({ withShadow }) => (withShadow ? "block" : "none")};
    }
  }
`;

const StyledStack = styled(Stack)`
  width: 100%;
  justify-content: space-between;

  & > div {
    margin-left: 0;
  }

  @media ${({ theme }) => theme.mq.max.tablet} {
    & > div:not(:first-child) {
      margin-left: 15px;
    }
  }
`;

export const HeaderCategoryNav = ({
  fontSize = 0,
  menuItems,
}: HeaderCategoryNavProps): JSX.Element => {
  const styledNavRef = useRef(null);
  const LOCATION = useLocation();
  const { pathname, search } = LOCATION;
  const isSpiritPage = hasSpiritsInPath(pathname);
  const [screeWidth] = useWindowSize();
  const { carryOverFilter } = useInstantSearch();
  const savedFilter = (carryOverFilter || search).replace(
    /(\?|&)(query|page)=[^&]+/g,
    ""
  );

  const MenuList = useMemo(() => {
    if (!menuItems) return [];

    const noFilterLink = ['vim', 'wine clubs', 'articles'];

    return menuItems.map((item, index) => {
      const filteredLink = noFilterLink.includes(item.label.toLowerCase())
        ? item.link
        : item.link.endsWith("/")
          ? `${item.link}${savedFilter}`
          : `${item.link}/${savedFilter}`;
      return <NavLink
        fontSize={fontSize}
        to={
          filteredLink
        }
        color="white"
        isExternal
        key={index}
        isWhiskymofoEnabled={isSpiritPage && WHISKYMOFO_HEADER_ENABLED}
        isActive={
          pathname === (item.link.endsWith("/") ? item.link : `${item.link}/`)
        }
      >
        {item.label}
      </NavLink>
    });
  }, [menuItems, LOCATION, carryOverFilter, isSpiritPage]);

  let withShadow = false;
  if (styledNavRef.current && screeWidth < styledNavRef.current.scrollWidth)
    withShadow = true;

  return (
    <StyledNav ref={styledNavRef} withShadow={withShadow}>
      <StyledStack space={[3, 3, 4]} horizontal>
        {MenuList}
      </StyledStack>
    </StyledNav>
  );
};
